/**
 * Index socket tick handling — same logic as fxbazzar_mobileview MarketWatch.js handleMessageRef.
 */
import { normalizeTickPayload } from "utils/socketTick";
import {
  canonicalBenchmarkIndexAlias,
  indexRowIdentityTokens,
  indexRowLtp,
  namesShareScriptFamily,
  pickReasonablePrevClose,
  rowBenchmarkIndexAlias,
} from "utils/indexStripRows";
import { recordTickToCacheEntry } from "utils/tickCache";

function parseTick(raw) {
  const data = normalizeTickPayload(raw);
  if (!data || Object.keys(data).length === 0) return null;

  const Script =
    data.Script ??
    data.script ??
    data.SCRIPT ??
    data.displayName ??
    data.DisplayName ??
    data.name ??
    data.Name ??
    data.Symbol ??
    data.symbol ??
    data.Token ??
    data.token ??
    data.instrumentToken ??
    data.InstrumentToken ??
    "";
  const scriptStr = String(Script).trim();
  if (!scriptStr) return null;

  const LTP = Number(
    data.LTP ??
      data.ltp ??
      data.Ltp ??
      data.lastTradedPrice ??
      data.LastTradedPrice ??
      data.lastPrice ??
      data.LastPrice ??
      data.close ??
      data.Close ??
      0
  );
  const PrevClose = Number(
    data.PrevClose ??
      data.prevClose ??
      data.prev_close ??
      data.previousClose ??
      data.PreviousClose ??
      data.close ??
      data.Close ??
      0
  );

  const tickCandidates = new Set();
  const addTick = (x) => {
    if (x == null || x === "") return;
    const s = String(x).trim();
    if (!s) return;
    tickCandidates.add(s);
    tickCandidates.add(s.toUpperCase());
  };
  addTick(scriptStr);
  addTick(Script);
  addTick(data.Script);
  addTick(data.script);
  addTick(data.Symbol);
  addTick(data.symbol);
  addTick(data.Token);
  addTick(data.token);
  addTick(data.instrumentToken);
  addTick(data.InstrumentToken);

  return {
    data,
    Script,
    scriptStr,
    LTP,
    PrevClose,
    tickCandidates,
    tickBenchmarkAlias: canonicalBenchmarkIndexAlias(scriptStr),
  };
}

function buildChgFmt(LTP, PrevClose) {
  return (row) => {
    const fromRow = Number(row?.__stablePrevClose ?? 0);
    const pc =
      Number.isFinite(fromRow) && fromRow > 0
        ? fromRow
        : (() => {
            const picked = pickReasonablePrevClose(LTP, PrevClose, row);
            return Number.isFinite(picked) && picked > 0 ? picked : 0;
          })();
    const chg = LTP - pc;
    return {
      prevClose: pc,
      changeAmount: chg.toFixed(2),
      changeAmountColor: chg >= 0 ? "text-success" : "text-danger",
      changePercent: LTP && pc && pc !== 0 ? (((LTP - pc) / pc) * 100).toFixed(2) : "0.00",
    };
  };
}

/** Same change math as mobile socket handler — use on API load + each tick. */
export function applyIndexRowSocketChange(row, ltp, prevCloseFromTick = 0) {
  if (!row || typeof row !== "object") return row;
  const LTP = Number(ltp);
  if (!Number.isFinite(LTP) || LTP <= 0) return row;
  const chg = buildChgFmt(LTP, prevCloseFromTick)(row);
  const prevLTP = Number(row.lastTradedPrice ?? row.ltp ?? row.LTP ?? 0);
  return {
    ...row,
    lastTradedPrice: LTP,
    close: chg.prevClose,
    __stablePrevClose: chg.prevClose,
    colorLTP:
      prevLTP > LTP ? "#BF2114" : prevLTP < LTP ? "#009846" : row.colorLTP || "",
    changeAmount: chg.changeAmount,
    changeAmountColor: chg.changeAmountColor,
    changePercent: chg.changePercent,
  };
}

/** After API + tick-cache merge, derive change % like the first mobile socket tick. */
export function bootstrapIndexRowFromApi(row) {
  const ltp = indexRowLtp(row);
  if (!ltp) return row;
  return applyIndexRowSocketChange(row, ltp, 0);
}

function applyIndexRowUpdate(idxRow, LTP, chg) {
  const prevLTP = idxRow.lastTradedPrice ?? 0;
  return {
    ...idxRow,
    lastTradedPrice: LTP,
    close: chg.prevClose,
    __stablePrevClose: chg.prevClose,
    colorLTP: prevLTP > LTP ? "#BF2114" : prevLTP < LTP ? "#009846" : idxRow.colorLTP || "",
    changeAmount: chg.changeAmount,
    changeAmountColor: chg.changeAmountColor,
    changePercent: chg.changePercent,
  };
}

function findIndexRow(idxMap, tick) {
  const { Script, scriptStr, tickCandidates, tickBenchmarkAlias } = tick;
  let idxKey = null;
  let idxRow = null;

  for (const [k, v] of idxMap) {
    for (const id of indexRowIdentityTokens(v)) {
      const u = id.toUpperCase();
      if (tickCandidates.has(id) || tickCandidates.has(u)) {
        idxKey = k;
        idxRow = v;
        break;
      }
    }
    if (idxKey) break;
  }

  if (!idxKey && tickBenchmarkAlias) {
    for (const [k, v] of idxMap) {
      if (rowBenchmarkIndexAlias(v) === tickBenchmarkAlias) {
        idxKey = k;
        idxRow = v;
        break;
      }
    }
  }

  if (!idxKey || !idxRow) {
    if (idxMap.has(Script) || idxMap.has(scriptStr)) {
      idxKey = idxMap.has(Script) ? Script : scriptStr;
      idxRow = idxMap.get(idxKey);
    } else {
      for (const [k, v] of idxMap) {
        if (
          v?.displayName === Script ||
          v?.script === Script ||
          k === Script ||
          v?.displayName === scriptStr ||
          v?.script === scriptStr ||
          k === scriptStr
        ) {
          idxKey = k;
          idxRow = v;
          break;
        }
      }
    }
  }

  if (!idxKey || !idxRow) {
    for (const [k, v] of idxMap) {
      if (v?.id != null && String(v.id) === scriptStr) {
        idxKey = k;
        idxRow = v;
        break;
      }
    }
  }

  return { idxKey, idxRow };
}

function syncIndexFromWatchlistRow(idxMap, existingRow, tick, chgFmt) {
  const { LTP } = tick;
  let syncKey = null;
  let syncRow = null;
  const dn = String(existingRow.displayName || "").trim();
  const sc = String(existingRow.script || "").trim();
  const sy = String(existingRow.symbol || "").trim();
  const wlTokSet = new Set(
    indexRowIdentityTokens(existingRow).map((t) => String(t).trim().toUpperCase())
  );

  for (const [k, v] of idxMap) {
    const hitTok = indexRowIdentityTokens(v).some((id) => {
      const u = String(id).trim().toUpperCase();
      return wlTokSet.has(u) || wlTokSet.has(String(id).trim());
    });
    if (hitTok) {
      syncKey = k;
      syncRow = v;
      break;
    }
  }

  if (!syncKey) {
    for (const [k, v] of idxMap) {
      const vd = String(v?.displayName || "").trim();
      const vs = String(v?.script || "").trim();
      const vy = String(v?.symbol || "").trim();
      if (
        (dn && (vd === dn || vs === dn || vy === dn)) ||
        (sc && (vd === sc || vs === sc || vy === sc)) ||
        (sy && (vd === sy || vs === sy || vy === sy))
      ) {
        syncKey = k;
        syncRow = v;
        break;
      }
    }
  }

  if (!syncKey || !syncRow) return null;
  return { syncKey, row: applyIndexRowUpdate(syncRow, LTP, chgFmt(syncRow)) };
}

function findWatchlistRow(currentMap, tick) {
  const { Script, scriptStr, tickCandidates } = tick;
  if (!(currentMap instanceof Map) || currentMap.size === 0) return null;

  if (currentMap.has(Script) || currentMap.has(scriptStr)) {
    const existingKey = currentMap.has(Script) ? Script : scriptStr;
    return { existingKey, existingRow: currentMap.get(existingKey) };
  }

  const wlNorm = scriptStr.toUpperCase();
  const scriptKey = wlNorm.replace(/\s+/g, "");

  for (const [k, v] of currentMap) {
    const d = (v?.displayName || "").toString().trim().toUpperCase();
    const s = (v?.script || "").toString().trim().toUpperCase();
    const sym = (v?.symbol || "").toString().trim().toUpperCase();
    const tokenHit = indexRowIdentityTokens(v).some(
      (id) => tickCandidates.has(id) || tickCandidates.has(id.toUpperCase())
    );
    const match =
      tokenHit ||
      v?.displayName === Script ||
      v?.script === Script ||
      v?.symbol === Script ||
      v?.displayName === scriptStr ||
      v?.script === scriptStr ||
      v?.symbol === scriptStr ||
      wlNorm === d ||
      wlNorm === s ||
      wlNorm === sym ||
      scriptKey === d.replace(/\s+/g, "") ||
      scriptKey === s.replace(/\s+/g, "") ||
      scriptKey === sym.replace(/\s+/g, "") ||
      namesShareScriptFamily(wlNorm, d) ||
      namesShareScriptFamily(wlNorm, s) ||
      namesShareScriptFamily(wlNorm, sym);
    if (match) return { existingKey: k, existingRow: v };
  }
  return null;
}

/**
 * Process one socket tick for benchmark indices (mobile-equivalent).
 * @returns {object|null} normalized tick for legacy watchlist effect
 */
export function processIndexSocketTick({
  raw,
  scriptIndexList,
  marketWatchList,
  tickCacheRef,
  onIndexMapUpdated,
}) {
  const tick = parseTick(raw);
  if (!tick) return null;

  const idxMap = scriptIndexList instanceof Map ? scriptIndexList : null;
  if (!idxMap || idxMap.size === 0) return tick.data;

  const chgFmt = buildChgFmt(tick.LTP, tick.PrevClose);
  let syncedBenchmarkIndex = false;

  const wlHit = findWatchlistRow(marketWatchList, tick);
  if (wlHit?.existingRow) {
    const sync = syncIndexFromWatchlistRow(idxMap, wlHit.existingRow, tick, chgFmt);
    if (sync) {
      const idxAlias = tick.tickBenchmarkAlias ? `IDX:${tick.tickBenchmarkAlias}` : null;
      if (tickCacheRef?.current) {
        recordTickToCacheEntry(tickCacheRef.current, sync.row, [sync.syncKey, idxAlias].filter(Boolean));
      }
      const newIdx = new Map(idxMap);
      newIdx.set(sync.syncKey, sync.row);
      onIndexMapUpdated(newIdx);
      syncedBenchmarkIndex = true;
    }
    // Same as mobile: watchlist ticks must not fall through to benchmark slots unless
    // this is a benchmark alias tick that failed to sync (e.g. futures stealing NIFTY).
    if (!tick.tickBenchmarkAlias || syncedBenchmarkIndex) return tick.data;
  }

  const { idxKey, idxRow } = findIndexRow(idxMap, tick);
  if (idxKey && idxRow) {
    const updated = applyIndexRowUpdate(idxRow, tick.LTP, chgFmt(idxRow));
    const idxAlias = tick.tickBenchmarkAlias ? `IDX:${tick.tickBenchmarkAlias}` : null;
    if (tickCacheRef?.current) {
      recordTickToCacheEntry(tickCacheRef.current, updated, [idxKey, idxAlias].filter(Boolean));
    }
    const newIdx = new Map(idxMap);
    newIdx.set(idxKey, updated);
    onIndexMapUpdated(newIdx);
  }

  return tick.data;
}
